import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { PageHeader, Card, money, fmtDate, Th, Td, IpTag, LeadTag, hasPendingLead } from "../components/shared";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ChevronLeft, ChevronRight, Mail, MousePointerClick, DollarSign, User, FileText } from "lucide-react";

const ICONS = {
  email_sent: { icon: Mail, cls: "bg-amber-50 text-amber-600" },
  click: { icon: MousePointerClick, cls: "bg-indigo-50 text-indigo-600" },
  lead: { icon: FileText, cls: "bg-sky-50 text-sky-600" },
  conversion: { icon: DollarSign, cls: "bg-emerald-50 text-emerald-600" },
};

export const JourneyDialog = ({ email, clickId, onClose }) => {
  const [data, setData] = useState(null);
  const key = email || clickId;
  useEffect(() => {
    if (!key) return;
    setData(null);
    const qp = email ? `email=${encodeURIComponent(email)}` : `click_id=${encodeURIComponent(clickId)}`;
    api.get(`/customers/journey?${qp}`).then((r) => setData(r.data));
  }, [email, clickId, key]);
  if (!key) return null;
  return (
    <Dialog open={!!key} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="customer-journey-dialog">
        <DialogHeader><DialogTitle className="font-heading">Customer Journey</DialogTitle></DialogHeader>
        {!data ? <div className="text-sm text-slate-400 animate-pulse py-4">Loading…</div> : (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center"><User className="h-5 w-5" /></div>
              <div>
                <div className="text-sm font-semibold text-slate-900">{data.name || data.lead_db?.name || "Unknown name"}</div>
                <code className="text-[11px] text-indigo-600">{data.email || "no email identity"}</code>
                <div className="mt-0.5"><LeadTag lead={data.lead_db} testId="journey-lead-tag" /></div>
              </div>
              <div className="ml-auto text-right">
                <div className="font-mono text-sm font-semibold text-emerald-600">{money(data.totals.revenue)}</div>
                <div className="text-[10px] text-slate-400">{data.totals.emails_sent} emails · {data.totals.clicks} clicks · {data.totals.leads} leads · {data.totals.conversions} conv</div>
              </div>
            </div>
            <div className="max-h-[380px] overflow-y-auto scrollbar-thin pr-1" data-testid="journey-timeline">
              {data.timeline.length === 0 && <div className="text-xs text-slate-400">No activity yet for this customer.</div>}
              {data.timeline.map((t, i) => {
                const cfg = ICONS[t.type];
                const Icon = cfg.icon;
                return (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${cfg.cls}`}><Icon className="h-4 w-4" /></div>
                      {i < data.timeline.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
                    </div>
                    <div className="pb-5 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{t.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5 break-all">{t.detail}</div>
                      {(t.type === "click" || t.type === "conversion") && "ip" in t && <div className="mt-1"><IpTag check={t.ip_check} ip={t.ip} testId={`journey-ip-${i}`} /></div>}
                      <div className="text-[10px] text-slate-400 mt-0.5">{fmtDate(t.timestamp)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Customers() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [journeyEmail, setJourneyEmail] = useState(null);
  const [tick, setTick] = useState(0);
  const limit = 25;

  const load = useCallback(() => {
    api.get(`/customers?search=${encodeURIComponent(search)}&limit=${limit}&skip=${page * limit}`).then((r) => setData(r.data));
  }, [search, page, tick]);

  useEffect(() => {
    if (!hasPendingLead(data.items)) return;
    const t = setTimeout(() => setTick((x) => x + 1), 15000);
    return () => clearTimeout(t);
  }, [data]);
  useEffect(load, [load]);

  return (
    <div data-testid="customers-page">
      <PageHeader title="Customers" subtitle={`${data.total.toLocaleString()} tracked customers — click a row for their complete journey from email sent to conversion`} />
      <div className="mb-4 fade-up">
        <Input data-testid="customers-search-input" placeholder="Search name or email…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="max-w-xs bg-white" />
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr><Th>Customer</Th><Th className="text-right">Emails Sent</Th><Th className="text-right">Clicks</Th><Th className="text-right">Leads</Th><Th className="text-right">Conversions</Th><Th className="text-right">Revenue</Th><Th>Last Activity</Th></tr></thead>
          <tbody data-testid="customers-table-body">
            {data.items.map((c) => (
              <tr key={c.email} data-testid={`customer-row-${c.email}`} className="border-t border-slate-100 hover:bg-indigo-50/40 cursor-pointer" onClick={() => setJourneyEmail(c.email)}>
                <Td>
                  <div className="font-medium text-slate-900">{c.name || "—"}</div>
                  <code className="text-[11px] text-indigo-600">{c.email}</code>
                  <div><LeadTag lead={c.lead_db} testId={`customer-lead-${c.email}`} /></div>
                </Td>
                <Td className="text-right font-mono">{c.emails_sent}</Td>
                <Td className="text-right font-mono">{c.clicks}</Td>
                <Td className="text-right font-mono">{c.leads}</Td>
                <Td className="text-right font-mono">{c.conversions}</Td>
                <Td className="text-right font-mono font-semibold text-emerald-600">{money(c.revenue)}</Td>
                <Td className="text-xs">{c.last_activity ? fmtDate(c.last_activity) : "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {data.items.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No customers found.</div>}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <span className="text-xs text-slate-400">Page {page + 1} of {Math.max(1, Math.ceil(data.total / limit))}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-3.5 w-3.5" /></Button>
            <Button variant="outline" size="sm" className="h-7" disabled={(page + 1) * limit >= data.total} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </Card>
      <JourneyDialog email={journeyEmail} onClose={() => setJourneyEmail(null)} />
    </div>
  );
}
